import { useEffect, useState } from 'react';
import axios from 'axios';

import { useHandSelection } from '../../hooks/project/useHandSelection';
import { captureFunnelEvent, trackButtonClick } from '../../services/telemetry';
import type { UploadKind } from '../../services/telemetry';
import type { Hand } from '../../types/types';

export interface SinglePartHandDialogProps {
  projectId: string;
  /** Upload kind, for telemetry only. */
  kind: UploadKind;
  /** Generation has been dispatched; the page should show progress. */
  onHandSelected: (hand: Hand) => void;
  /** A non-piano instrument was reported; the project is a dead end. */
  onInstrumentReported: () => void;
  /**
   * Already recorded via a prior ``report-instrument`` call. When set, open
   * on the thanks view so returning to the project does not re-ask.
   */
  reportedInstrument?: string | null;
}

type Choice = Hand | 'other';

/** Must match backend ``INSTRUMENT_NAME_MAX_LENGTH``. */
const MAX_INSTRUMENT_LENGTH = 64;

const CHOICES: { value: Choice; label: string }[] = [
  { value: 'right', label: 'Piano right hand' },
  { value: 'left', label: 'Piano left hand' },
  { value: 'other', label: "It's for another instrument" },
];

/**
 * Asks which hand a single-staff score is for before credit is spent.
 * Right hand is the default. There is no dismiss control — use "another
 * instrument" to exit without generating.
 */
export const SinglePartHandDialog = ({
  projectId,
  kind,
  onHandSelected,
  onInstrumentReported,
  reportedInstrument = null,
}: SinglePartHandDialogProps) => {
  const [view, setView] = useState<'choose' | 'instrument' | 'reported'>(
    reportedInstrument ? 'reported' : 'choose'
  );
  const [choice, setChoice] = useState<Choice>('right');
  const [instrument, setInstrument] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Stays true after a successful hand Continue so the button does not
  // flicker back on when `isSubmitting` clears — the dialog is still up
  // until polling sees the new status.
  const [handSubmitted, setHandSubmitted] = useState(false);
  const { selectHand, reportInstrument, isSubmitting } = useHandSelection();

  useEffect(() => {
    if (reportedInstrument) setView('reported');
  }, [reportedInstrument]);

  useEffect(() => {
    captureFunnelEvent('single_part_dialog_shown', { projectId, kind });
  }, [projectId, kind]);

  const failureProps = (err: unknown) => {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    return {
      projectId,
      kind,
      httpStatus: status ?? null,
      reason: status != null ? `http_${status}` : 'exception',
    };
  };

  const handleHand = async (hand: Hand) => {
    if (isSubmitting || handSubmitted) return;
    setHandSubmitted(true);
    setError(null);
    trackButtonClick('SinglePartHandDialog', `Hand: ${hand}`, { projectId });
    try {
      await selectHand(projectId, hand);
      captureFunnelEvent('single_part_hand_selected', {
        projectId,
        kind,
        hand,
      });
      onHandSelected(hand);
    } catch (err) {
      setHandSubmitted(false);
      captureFunnelEvent('single_part_hand_select_failed', failureProps(err));
      // After a failed dispatch the project is no longer in
      // AWAITING_HAND_SELECTION, so retrying this dialog will 409 — re-upload.
      setError(
        'Could not start generation. Please check your remaining credits and re-upload the score.'
      );
    }
  };

  const handleContinue = () => {
    if (isSubmitting || handSubmitted) return;
    if (choice === 'other') {
      trackButtonClick('SinglePartHandDialog', 'Another instrument', {
        projectId,
      });
      setError(null);
      setView('instrument');
      return;
    }
    void handleHand(choice);
  };

  const handleInstrument = async () => {
    const trimmed = instrument.trim();
    if (!trimmed || isSubmitting) return;
    setError(null);
    try {
      await reportInstrument(projectId, trimmed);
      captureFunnelEvent('single_part_instrument_reported', {
        projectId,
        kind,
        instrument: trimmed,
      });
      setView('reported');
    } catch (err) {
      captureFunnelEvent(
        'single_part_instrument_report_failed',
        failureProps(err)
      );
      setError('Could not send that. Please try again.');
    }
  };

  const primaryButtonClass = `
    cursor-pointer rounded-full bg-gray-800 px-5 py-2 font-medium text-white
    transition-all duration-100
    hover:bg-gray-700
    active:scale-95
    disabled:cursor-not-allowed disabled:opacity-50
  `;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={
          view === 'instrument' ? undefined : 'single-part-dialog-title'
        }
        aria-label={view === 'instrument' ? 'Report instrument' : undefined}
      >
        {view !== 'instrument' && (
          <h2
            id="single-part-dialog-title"
            className="mb-2 text-xl font-semibold text-gray-900"
          >
            {view === 'reported'
              ? 'Thanks for telling us!'
              : 'Only single staff is detected'}
          </h2>
        )}

        {view === 'choose' && (
          <>
            <p className="mb-6 text-gray-600">
              Please select which hand you would like to get fingerings for.
            </p>
            <fieldset
              className="mb-6 flex flex-col gap-3"
              disabled={isSubmitting || handSubmitted}
            >
              <legend className="sr-only">Hand for this score</legend>
              {CHOICES.map((option) => (
                <label
                  key={option.value}
                  className={`
                    flex cursor-pointer items-center rounded-lg border p-4
                    transition-colors
                    ${
                      choice === option.value
                        ? 'border-yellow-500 bg-yellow-50'
                        : `
                          border-gray-300
                          hover:border-gray-400 hover:bg-gray-50
                        `
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="single-part-hand"
                    value={option.value}
                    checked={choice === option.value}
                    onChange={() => setChoice(option.value)}
                    className={`
                      h-5 w-5 cursor-pointer text-yellow-500
                      focus:ring-2 focus:ring-yellow-500
                    `}
                  />
                  <span className="ml-3 text-lg text-gray-800">
                    {option.label}
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="flex justify-end">
              <button
                type="button"
                disabled={isSubmitting || handSubmitted}
                onClick={handleContinue}
                className={primaryButtonClass}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {view === 'instrument' && (
          <>
            <p className="mb-4 text-gray-600">
              We only support piano fingerings today. Please input your
              instrument to help us decide what to support next.
            </p>
            <input
              type="text"
              value={instrument}
              maxLength={MAX_INSTRUMENT_LENGTH}
              onChange={(e) => setInstrument(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInstrument();
              }}
              placeholder="e.g. violin, flute, guitar"
              aria-label="Instrument"
              className={`
                mb-4 w-full rounded-lg border border-gray-300 p-3 text-lg
                focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500
                focus:outline-none
              `}
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setView('choose')}
                className={`
                  cursor-pointer rounded-full border border-gray-800
                  bg-transparent px-5 py-2 font-medium text-gray-800
                  transition-all duration-100
                  hover:bg-gray-800/5
                  active:scale-95
                `}
              >
                Back
              </button>
              <button
                type="button"
                disabled={isSubmitting || !instrument.trim()}
                onClick={handleInstrument}
                className={primaryButtonClass}
              >
                Send
              </button>
            </div>
          </>
        )}

        {view === 'reported' && (
          <>
            <p className="mb-6 text-gray-600">
              We&apos;ve noted your request. Feel free to come back and pick a
              hand to see piano fingerings, pitch label and sharp / flat
              highlight anytime.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setView('choose')}
                className={`
                  cursor-pointer rounded-full border border-gray-800
                  bg-transparent px-5 py-2 font-medium text-gray-800
                  transition-all duration-100
                  hover:bg-gray-800/5
                  active:scale-95
                `}
              >
                Choose a hand
              </button>
              <button
                type="button"
                onClick={() => {
                  captureFunnelEvent('single_part_dialog_abandoned', {
                    projectId,
                    kind,
                    via: 'workspace',
                  });
                  onInstrumentReported();
                }}
                className={primaryButtonClass}
              >
                Back to workspace
              </button>
            </div>
          </>
        )}

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
};
